/**
 * Singleton Supabase client.
 *
 * Imported by every module that needs to talk to the backend. Centralising
 * here keeps auth options consistent and makes mocking easier in tests.
 */
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_URL, SUPABASE_ANON_KEY, LS_STATE_KEY } from "./config";

export const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/** Sign out reliably under heavy realtime load.
 *
 *  The straight-forward `sb.auth.signOut()` path was hanging silently
 *  for some users during the test campaign — they clicked the button
 *  and nothing happened. Same root cause as the calendar-save hangs
 *  we fixed earlier: the SDK's auth helpers compete with the realtime
 *  channel for the `navigator.locks` "sb-<ref>-auth-token" resource.
 *  With ~100 concurrent users banging realtime, that lock is held
 *  often enough that signOut queues for tens of seconds. No
 *  SIGNED_OUT event fires until the SDK call resolves, so the UI
 *  stays on the app shell and the button looks unresponsive.
 *
 *  This implementation goes around the SDK entirely:
 *    1. Clear the cached JWT from localStorage so the next page load
 *       is unauthenticated regardless of what happens server-side.
 *    2. Fire a best-effort POST to /auth/v1/logout via raw fetch
 *       with `keepalive: true` so the request survives the page
 *       unload below. This is what tells Supabase to invalidate the
 *       refresh token. No await — we never block the UI on this.
 *    3. Reload the page. Tears down realtime, pending pushes, retry
 *       timers, in-flight fetches — everything — without us having
 *       to coordinate teardown across modules. Lands on the Login
 *       screen since the JWT is gone.
 *
 *  Returns a Promise<void> for call-site compatibility (boot.ts still
 *  uses `await signOut()`), but the body is fully synchronous — the
 *  promise resolves before the reload fires. */
export async function signOut(): Promise<void> {
  const ref = new URL(SUPABASE_URL).hostname.split(".")[0];
  const tokenKey = `sb-${ref}-auth-token`;

  // Snapshot the access_token BEFORE clearing, so step 2 can use it
  // to authenticate the logout call.
  let accessToken: string | null = null;
  try {
    const raw = localStorage.getItem(tokenKey);
    if (raw) {
      const parsed = JSON.parse(raw) as { access_token?: string };
      accessToken = parsed.access_token ?? null;
    }
  } catch {
    /* corrupted blob — falls through to clear + reload */
  }

  // 1. Local clear. Guarantees the next page load is unauthenticated
  //    even if the server call below never completes.
  try {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(LS_STATE_KEY);
  } catch {
    /* quota / private mode — reload will still produce a clean slate */
  }

  // 2. Best-effort server-side refresh-token invalidation. keepalive=true
  //    is the magic word: without it, location.reload() below would
  //    cancel the in-flight fetch and the refresh token would stay
  //    valid until its natural expiry. With it, the browser commits
  //    to completing the request even as the page unloads. Body size
  //    limits don't matter here — there's no body.
  if (accessToken) {
    fetch(`${SUPABASE_URL}/auth/v1/logout`, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      keepalive: true,
    }).catch(() => {
      /* best effort — refresh token will expire naturally if this fails */
    });
  }

  // 3. Reload. setTimeout(0) lets the fetch above dispatch on this
  //    event-loop tick before the page tears down — without the yield,
  //    some browsers cancel the fetch before keepalive can engage.
  setTimeout(() => location.reload(), 0);
}
